import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaRecordVinyl, FaHeadphones, FaCompactDisc } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from './SEO';
import './UndergroundPage.css';

const UndergroundPage = () => {
  const { t } = useLanguage();
  
  // Refs для каждого Spotify iframe
  const player1Ref = useRef(null);
  const player2Ref = useRef(null);
  const player3Ref = useRef(null);
  
  // URLs для каждого плеера (для перезагрузки)
  const playerUrls = [
    'https://embed.beatport.com/?id=3592392&type=release', // Beatport для Abracadabra
    'https://embed.beatport.com/?id=3592403&type=release', // Beatport для Abracadabra (Remixes)
    'https://embed.beatport.com/?id=3592398&type=release' // Beatport для Dharma
  ];
  
  // Состояние активного плеера
  const [activePlayer, setActivePlayer] = useState(null);
  
  // Функция для остановки всех плееров кроме активного
  const stopOtherPlayers = (activeIndex) => {
    const players = [player1Ref, player2Ref, player3Ref];
    
    players.forEach((playerRef, index) => {
      if (index !== activeIndex && playerRef.current) {
        const iframe = playerRef.current.querySelector('iframe');
        if (iframe) {
          try {
            // Пытаемся отправить команду паузы через postMessage
            iframe.contentWindow.postMessage({ 
              command: 'pause',
              type: 'command'
            }, 'https://open.spotify.com');
          } catch (e) {
            // Если не получается, перезагружаем iframe для остановки
            const currentSrc = iframe.src;
            // Убираем параметры автозапуска если есть
            const newSrc = currentSrc.split('&')[0] + '?utm_source=generator&theme=0';
            iframe.src = '';
            setTimeout(() => {
              iframe.src = newSrc;
            }, 150);
          }
        }
      }
    });
    
    setActivePlayer(activeIndex);
  };
  
  // Обработчик клика на плеер
  const handlePlayerClick = (playerIndex) => {
    if (activePlayer !== playerIndex) {
      stopOtherPlayers(playerIndex);
    }
  };
  
  // Обработчик взаимодействия с плеером
  const handlePlayerInteraction = useCallback((playerIndex) => {
    if (activePlayer !== playerIndex) {
      stopOtherPlayers(playerIndex);
    }
  }, [activePlayer]);
  
  // Слушаем сообщения от Spotify iframe для отслеживания воспроизведения
  useEffect(() => {
    const handleMessage = (event) => {
      // Проверяем, что сообщение от Spotify
      if (event.origin !== 'https://open.spotify.com') return;
      
      // Определяем, какой плеер отправил сообщение
      const players = [player1Ref, player2Ref, player3Ref];
      players.forEach((playerRef, index) => {
        if (playerRef.current) {
          const iframe = playerRef.current.querySelector('iframe');
          if (iframe && iframe.contentWindow === event.source) {
            // Если плеер начал воспроизведение, останавливаем остальные
            if (event.data && (event.data.type === 'play' || event.data.command === 'play')) {
              stopOtherPlayers(index);
            }
          }
        }
      });
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [activePlayer]);
  
  // Отслеживаем клики внутри iframe через периодическую проверку
  useEffect(() => {
    const checkPlayers = () => {
      const players = [player1Ref, player2Ref, player3Ref];
      players.forEach((playerRef, index) => {
        if (playerRef.current) {
          const iframe = playerRef.current.querySelector('iframe');
          if (iframe) {
            // Добавляем обработчик клика на контейнер плеера
            const container = playerRef.current;
            if (!container.dataset.listenerAdded) {
              container.addEventListener('click', () => handlePlayerInteraction(index));
              container.dataset.listenerAdded = 'true';
            }
          }
        }
      });
    };
    
    // Проверяем при монтировании и периодически
    checkPlayers();
    const interval = setInterval(checkPlayers, 1000);
    
    return () => clearInterval(interval);
  }, [handlePlayerInteraction]);
  
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } }
  };

  return (
    <>
      <SEO 
        title="Underground Projects"
        description="Underground Electronic Music by Sergey Levitskiy - Deep, experimental sounds exploring the boundaries of electronic music. Original productions and remixes."
        keywords="Underground Music, Electronic Music, Techno, Deep House, Experimental Music, Sergey Levitskiy underground"
      />
      <div className="underground-page">
      {/* Креативные эффекты фона для Underground */}
      <div className="underground-waves"></div>
      <div className="underground-energy"></div>
      
      <motion.div
        className="underground-content"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
      >
        {/* Hero Section */}
        <motion.section
          className="underground-hero"
          variants={sectionVariants}
        >
          <div className="hero-decoration"></div>
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="hero-icon"
              animate={{ 
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              <FaHeadphones />
            </motion.div>
            <h1 className="underground-title">{t('underground.title')}</h1>
            <p className="underground-subtitle">{t('underground.subtitle')}</p>
            <div className="hero-divider"></div>
          </motion.div>
        </motion.section>

        {/* Main Description */}
        <motion.section
          className="description-section"
          variants={sectionVariants}
        >
          <div className="description-content">
            <h2 className="section-title">{t('underground.mainTitle')}</h2>
            <div className="description-text">
              <p>
                {t('underground.description1')}
              </p>
              <p>
                {t('underground.description2')}
              </p>
              <p>
                {t('underground.description3')}
              </p>
              <p>
                {t('underground.description4')}
              </p>
            </div>
          </div>
        </motion.section>

        {/* Discography Section */}
        <motion.section
          className="discography-section"
          variants={sectionVariants}
        >
          <h2 className="section-title">Discography</h2>
          <div className="discography-grid">
            <motion.div
              className="album-item"
              variants={itemVariants}
            >
              <motion.div
                className="album-cover"
                whileHover={{ scale: 1.05, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="/images/PN002.jpg" 
                  alt="ABRACADABRA"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement.querySelector('.album-placeholder');
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }}
                  onLoad={(e) => {
                    const placeholder = e.target.parentElement.querySelector('.album-placeholder');
                    if (placeholder) {
                      placeholder.style.display = 'none';
                    }
                  }}
                />
                <div className="album-placeholder">
                  <FaRecordVinyl />
                  <span>ABRACADABRA</span>
                </div>
              </motion.div>
              <div 
                className={`spotify-player beatport-player ${activePlayer === 0 ? 'active' : ''}`}
                ref={player1Ref}
                onClick={() => handlePlayerClick(0)}
                onMouseEnter={() => handlePlayerInteraction(0)}
                style={{ cursor: 'pointer' }}
              >
                <iframe
                  src={playerUrls[0]}
                  width="100%"
                  height="362"
                  frameBorder="0"
                  scrolling="no"
                  style={{ maxWidth: '600px' }}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title="ABRACADABRA"
                ></iframe>
              </div>
            </motion.div>
            <motion.div
              className="album-item"
              variants={itemVariants}
            >
              <motion.div
                className="album-cover"
                whileHover={{ scale: 1.05, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="/images/PN005.jpg" 
                  alt="ABRACADABRA Remixes"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement.querySelector('.album-placeholder');
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }}
                  onLoad={(e) => {
                    const placeholder = e.target.parentElement.querySelector('.album-placeholder');
                    if (placeholder) {
                      placeholder.style.display = 'none';
                    }
                  }}
                />
                <div className="album-placeholder">
                  <FaRecordVinyl />
                  <span>ABRACADABRA Remixes</span>
                </div>
              </motion.div>
              <div 
                className={`spotify-player beatport-player ${activePlayer === 1 ? 'active' : ''}`}
                ref={player2Ref}
                onClick={() => handlePlayerClick(1)}
                onMouseEnter={() => handlePlayerInteraction(1)}
                style={{ cursor: 'pointer' }}
              >
                <iframe
                  src={playerUrls[1]}
                  width="100%"
                  height="362"
                  frameBorder="0"
                  scrolling="no"
                  style={{ maxWidth: '600px' }}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title="ABRACADABRA Remixes"
                ></iframe>
              </div>
            </motion.div>
            <motion.div
              className="album-item"
              variants={itemVariants}
            >
              <motion.div
                className="album-cover"
                whileHover={{ scale: 1.05, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="/images/newpn006.jpg" 
                  alt="DHARMA"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement.querySelector('.album-placeholder');
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }}
                  onLoad={(e) => {
                    const placeholder = e.target.parentElement.querySelector('.album-placeholder');
                    if (placeholder) {
                      placeholder.style.display = 'none';
                    }
                  }}
                />
                <div className="album-placeholder">
                  <FaRecordVinyl />
                  <span>DHARMA</span>
                </div>
              </motion.div>
              <div 
                className={`spotify-player beatport-player ${activePlayer === 2 ? 'active' : ''}`}
                ref={player3Ref}
                onClick={() => handlePlayerClick(2)}
                onMouseEnter={() => handlePlayerInteraction(2)}
                style={{ cursor: 'pointer' }}
              >
                <iframe
                  src={playerUrls[2]}
                  width="100%"
                  height="362"
                  frameBorder="0"
                  scrolling="no"
                  style={{ maxWidth: '600px' }}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title="DHARMA"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Philosophy Section */}
        <motion.section
          className="philosophy-section"
          variants={sectionVariants}
        >
          <div className="philosophy-content">
            <div className="philosophy-icon">
              <FaCompactDisc />
            </div>
            <h2 className="section-title">{t('underground.philosophy')}</h2>
            <div className="philosophy-text">
              <p>
                {t('underground.philosophy1')}
              </p>
              <p>
                {t('underground.philosophy2')}
              </p>
              <p>
                {t('underground.philosophy3')}
              </p>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className="cta-section"
          variants={sectionVariants}
        >
          <div className="cta-content">
            <h2>{t('underground.cta')}</h2>
            <p>{t('underground.ctaDesc')}</p>
            <Link to="/player" className="cta-button">
              {t('underground.listen')}
            </Link>
          </div>
        </motion.section>
      </motion.div>
    </div>
    </>
  );
};

export default UndergroundPage;

